import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Repeat, Star, MapPin, Download } from 'lucide-react';
import api from '../lib/api';
import MonthView from '../components/calendar/MonthView';
import WeekView from '../components/calendar/WeekView';

/**
 * Calendar — Q32 Phase A + Phase B.
 *
 * Three switchable views:
 *   • Agenda — chronological day-grouped list (Phase A).
 *   • Week   — Google-Cal-style timeline (Phase B).
 *   • Month  — mini grid with dots + recurring chip (Phase B).
 *
 * Recurring plans are expanded server-side into their next N occurrences,
 * so every view re-uses the same `items` array.
 */
const VIEWS = [
  { key: 'agenda', label: 'Agenda' },
  { key: 'week', label: 'Week' },
  { key: 'month', label: 'Month' },
];

export default function CalendarPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [view, setView] = useState(() => localStorage.getItem('cal:view') || 'agenda');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/calendar/agenda?limit=120');
        setItems(data.items || []);
      } catch { /* silent — empty state takes over */ }
      setLoading(false);
    })();
  }, []);

  useEffect(() => { localStorage.setItem('cal:view', view); }, [view]);

  // Group rows by date for agenda + drive Month/Week lookups
  const itemsByDate = useMemo(() => {
    return items.reduce((acc, row) => {
      (acc[row.date] = acc[row.date] || []).push(row);
      return acc;
    }, {});
  }, [items]);

  const sortedDates = useMemo(() => Object.keys(itemsByDate).sort(), [itemsByDate]);

  const downloadAll = async () => {
    setDownloading(true);
    try {
      const res = await api.get('/calendar/ics/all', { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/calendar' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'asap-calendar.ics';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { /* swallow — toast surfaces no value, agenda already loaded */ }
    setDownloading(false);
  };

  return (
    <div className="content-area pb-12" data-testid="calendar-page">
      <div className="px-4 pt-5 pb-3 flex items-center gap-3">
        <Calendar size={20} className="text-[#3F5200]" />
        <h1 className="text-lg font-bold tracking-tight text-[#111111] flex-1">My calendar</h1>
        <button
          data-testid="calendar-download-all"
          onClick={downloadAll}
          disabled={downloading || items.length === 0}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#111] text-white text-xs font-bold disabled:opacity-50"
        >
          <Download size={13} />
          {downloading ? 'Saving…' : 'Export .ics'}
        </button>
      </div>

      {/* View tabs */}
      <div className="px-4 mb-3">
        <div
          data-testid="calendar-view-tabs"
          className="inline-flex bg-[#F1F0EA] rounded-full p-1 gap-1"
          role="tablist"
        >
          {VIEWS.map(v => (
            <button
              key={v.key}
              role="tab"
              aria-selected={view === v.key}
              data-testid={`view-tab-${v.key}`}
              onClick={() => setView(v.key)}
              className={`px-4 py-1.5 rounded-full text-xs font-bold transition-colors
                ${view === v.key ? 'bg-white text-[#111] shadow-sm' : 'text-[#6B6E73]'}`}
            >
              {v.label}
            </button>
          ))}
        </div>
      </div>

      <div className="px-4 space-y-5">
        {loading ? (
          <p className="text-center text-sm text-[#9B9EA3] py-8" data-testid="calendar-loading">Loading…</p>
        ) : items.length === 0 ? (
          <EmptyState onExplore={() => navigate('/')} />
        ) : view === 'month' ? (
          <MonthView itemsByDate={itemsByDate} />
        ) : view === 'week' ? (
          <WeekView itemsByDate={itemsByDate} />
        ) : (
          sortedDates.map(d => (
            <section key={d} data-testid={`calendar-day-${d}`}>
              <p className="text-[10px] font-extrabold text-[#9B9EA3] uppercase tracking-wider mb-2">
                {formatDayLabel(d)}
              </p>
              <div className="space-y-2">
                {itemsByDate[d].map((row, idx) => (
                  <AgendaRow key={`${row.plan.id}-${idx}`} row={row} onTap={() => navigate(`/plan/${row.plan.id}`)} />
                ))}
              </div>
            </section>
          ))
        )}
      </div>
    </div>
  );
}

function EmptyState({ onExplore }) {
  return (
    <div data-testid="calendar-empty" className="bg-white rounded-3xl border border-black/[0.06] p-6 text-center">
      <Calendar size={22} className="mx-auto text-[#B0B3B8] mb-2" />
      <h2 className="text-base font-extrabold text-[#111]">Nothing scheduled</h2>
      <p className="text-xs text-[#6B6E73] mt-1 leading-relaxed">
        Join or host a plan to see it here.
      </p>
      <button
        data-testid="calendar-empty-cta"
        onClick={onExplore}
        className="mt-4 w-full bg-[#B6E600] text-[#111] font-bold rounded-full py-2.5 text-sm"
      >
        Explore plans
      </button>
    </div>
  );
}

function formatDayLabel(iso) {
  try {
    const d = new Date(iso + 'T00:00:00');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tom = new Date(today); tom.setDate(tom.getDate() + 1);
    if (d.getTime() === today.getTime()) return 'Today';
    if (d.getTime() === tom.getTime()) return 'Tomorrow';
    return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  } catch { return iso; }
}

function AgendaRow({ row, onTap }) {
  const { plan } = row;
  return (
    <button
      data-testid={`agenda-row-${plan.id}`}
      onClick={onTap}
      className="w-full flex items-center gap-3 bg-white rounded-2xl border border-black/[0.06] p-3 active:bg-[#F1F0EA]"
    >
      <div className="w-10 h-10 rounded-xl bg-[#F1F0EA] text-[#111] flex items-center justify-center text-xs font-extrabold flex-shrink-0">
        {(plan.start_time || '').slice(0, 5) || '—'}
      </div>
      <div className="flex-1 text-left min-w-0">
        <div className="flex items-center gap-1.5">
          <p className="text-sm font-extrabold text-[#111] truncate flex-1">{plan.title}</p>
          {row.is_host && (
            <span data-testid="agenda-host-badge" title="You host this" className="text-[#FFC107]">
              <Star size={12} fill="currentColor" />
            </span>
          )}
          {row.is_recurring && (
            <span data-testid="agenda-recurring-badge" title="Recurring" className="text-[#3F5200]">
              <Repeat size={12} strokeWidth={2.6} />
            </span>
          )}
        </div>
        {(plan.venue || plan.city) && (
          <p className="text-[11px] text-[#6B6E73] truncate flex items-center gap-1 mt-0.5">
            <MapPin size={11} />{plan.venue || plan.city}
          </p>
        )}
      </div>
    </button>
  );
}
